import { useState, useEffect, useCallback } from 'react';
import { ApiError } from '../../services/api';

export interface PrimeStatsData<T> {
  loading: boolean;
  error: string | null;
  data: T | null;
  refresh: () => void;
}

interface State<T> {
  key: string;
  loading: boolean;
  error: string | null;
  data: T | null;
}

function errMessage(err: unknown): string {
  return err instanceof ApiError ? err.message : (err instanceof Error ? err.message : 'Der opstod en fejl');
}

/**
 * Generisk async-loader. `load` er null når der ikke skal hentes (guard).
 * `depKey` gentrigger fetch når den ændrer sig (skærm/rolle/salon-skift).
 *
 * Tilstanden bærer den `key` (depKey) som dataen tilhører. Ved render
 * returneres data KUN hvis `state.key === depKey` — ellers (depKey lige
 * skiftet, effekten ikke kørt endnu, eller et forsinket svar fra en gammel
 * nøgle) returneres en tom loading-tilstand. Det forhindrer at containeren
 * caster forrige svar-form (fx ChainStats som SalonStats ved chain→salon-drill).
 */
export function usePrimeStatsData<T>(load: (() => Promise<T>) | null, depKey: string): PrimeStatsData<T> {
  const [state, setState] = useState<State<T>>({ key: depKey, loading: !!load, error: null, data: null });

  const run = useCallback(async () => {
    if (!load) { setState({ key: depKey, loading: false, error: null, data: null }); return; }
    setState({ key: depKey, loading: true, error: null, data: null });
    try {
      const value = await load();
      setState({ key: depKey, loading: false, error: null, data: value });
    } catch (err) {
      setState({ key: depKey, loading: false, error: errMessage(err), data: null });
    }
    // load-identitet skifter pr. render; depKey styrer reel gentrigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depKey]);

  useEffect(() => { run(); }, [run]);

  if (state.key === depKey) {
    return { loading: state.loading, error: state.error, data: state.data, refresh: run };
  }
  // depKey er netop skiftet — eksponér aldrig stale data fra den forrige nøgle.
  return { loading: !!load, error: null, data: null, refresh: run };
}
